/**
 * 모달 다이얼로그 컴포넌트
 *
 * 다이얼로그, 확인창, 정보 표시 등에 사용하는 모달 컴포넌트입니다.
 * 디자인 시스템의 card 스타일을 활용하며, ESC 키로 닫기, 배경 클릭 닫기, 스크롤 방지 등의 기능을 제공합니다.
 * 접근성 속성(aria-modal, role="dialog")이 포함되어 있습니다.
 *
 * @component
 * @example
 * // 기본 모달
 * const [isOpen, setIsOpen] = useState(false);
 * <AppModal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="확인"
 * >
 *   이 작업을 진행하시겠습니까?
 * </AppModal>
 *
 * @example
 * // 버튼이 있는 모달
 * <AppModal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="삭제 확인"
 *   size="sm"
 *   footer={
 *     <>
 *       <AppButton variant="outline" onClick={() => setIsOpen(false)}>취소</AppButton>
 *       <AppButton variant="primary" onClick={handleDelete}>삭제</AppButton>
 *     </>
 *   }
 * >
 *   정말 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
 * </AppModal>
 *
 * @typedef {object} AppModalProps
 * @property {boolean} isOpen - 모달 열림/닫힘 상태
 * @property {() => void} onClose - 모달을 닫을 때 호출되는 콜백 함수
 * @property {React.ReactNode} [title] - 모달 제목 (헤더 생략 시 표시 안 됨)
 * @property {React.ReactNode} children - 모달 본문 컨텐츠
 * @property {React.ReactNode} [footer] - 모달 푸터 컨텐츠 (버튼 등)
 * @property {'sm' | 'md' | 'lg'} [size='md'] - 모달 크기
 * @property {boolean} [closeButton=true] - 제목 옆의 닫기 버튼 표시 여부
 * @property {string} [className] - 모달 콘텐츠에 추가할 CSS 클래스
 * @property {string} [overlayClassName] - 배경 오버레이에 추가할 CSS 클래스
 * @property {string} [contentClassName] - 본문 영역에 추가할 CSS 클래스
 *
 * @returns {JSX.Element | null} 모달 엘리먼트 (isOpen이 false이면 null)
 */

import { ReactNode, useEffect, useId, useRef } from 'react';
import { cn } from '@/lib/utils';

export interface AppModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  closeButton?: boolean;
  className?: string;
  overlayClassName?: string;
  contentClassName?: string;
}

const sizeMap = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
};

export function AppModal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  closeButton = true,
  className,
  overlayClassName,
  contentClassName,
}: AppModalProps) {
  const titleId = useId();
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Esc 키로 닫기 + 포커스 관리
  useEffect(() => {
    if (!isOpen) {return;}

    // 모달 열릴 때 이전 포커스 요소 저장
    previousFocusRef.current = document.activeElement as HTMLElement | null;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    // 스크롤 방지
    document.body.style.overflow = 'hidden';

    // 모달 내부로 포커스 이동
    requestAnimationFrame(() => {
      dialogRef.current?.focus();
    });

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';

      // 모달 닫힐 때 이전 포커스 복원
      previousFocusRef.current?.focus();
    };
  }, [isOpen, onClose]);

  if (!isOpen) {return null;}

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center bg-black/50',
        'transition-opacity duration-300',
        'p-4',
        overlayClassName
      )}
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className={cn(
          'w-full card animate-fade-in flex flex-col',
          'max-h-[calc(100dvh-2rem)]',
          'overflow-hidden',
          sizeMap[size],
          className
        )}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
      >
        {/* Header */}
        {title && (
          <div className="card-header flex items-center justify-between shrink-0">
            <h2 id={titleId} className="heading-3">
              {title}
            </h2>
            {closeButton && (
              <button
                onClick={onClose}
                className={cn(
                  'rounded-lg p-1',
                  'text-neutral-500 hover:text-neutral-700',
                  'hover:bg-neutral-100',
                  'transition-colors duration-base',
                  'focus:outline-none focus:ring-2 focus:ring-primary-500'
                )}
                aria-label="Close modal"
              >
                <span className="text-xl">✕</span>
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div
          className={cn(
            'card-content flex-1 overflow-y-auto overscroll-contain',
            contentClassName
          )}
        >
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="card-footer flex items-center justify-end gap-3 shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
